/**
 * @file        src/pages/seo/SeoPageEditor.jsx
 * @description SEO Management → Page SEO → one page (/seo/pages/:pageKey).
 *              Four blocks — Search, Social, Indexing, Structured data — beside
 *              a live Google result and a 1200×630 social card.
 *
 *              BOTH PREVIEWS COME FROM THE SERVER. The draft is debounced
 *              (~400ms) and POSTed to /api/seo/preview, which runs the
 *              storefront's own resolver over it; the cards render that answer.
 *              Rendering them from the form would show a title without the
 *              brand template, a description that is never truncated, and an
 *              image that never falls back — a preview that agrees with the
 *              admin instead of with Google.
 *
 *              Every empty field says what it falls back to ("empty → …"),
 *              from the API's `fallbacks`, so leaving a field blank is a choice
 *              the admin can see the consequence of.
 * @author      Kinetika Labs
 * @created     2026-10-09
 * @changelog
 *   - 2026-10-09 Claude — Initial (SEO Management).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import PageHead from '../../components/PageHead';
import Icon from '../../components/Icon';
import Select from '../../components/Select';
import ImageUpload from '../../components/ImageUpload';
import { getSeoPage, previewSeo, resetSeoPage, saveSeoPage } from '../../api/seo';
import { qk } from '../../api/queryKeys';
import { toast } from '../../lib/toast';
import { confirm } from '../../lib/confirm';
import {
  BrandBar, Counter, DESCRIPTION_LIMIT, Fallback, Field, GooglePreview, NoindexBanner, PickBrand,
  SectionTitle, SocialPreview, TITLE_LIMIT, errFields, errMessage, useDebounced, useSeoBrand, withBrand,
} from './seoShared';

const TEXT_KEYS = [
  'title', 'meta_description', 'meta_keywords', 'h1', 'canonical_url',
  'og_title', 'og_description', 'og_image_url', 'og_type',
  'twitter_card', 'twitter_title', 'twitter_description', 'twitter_image_url',
  'structured_data', 'sitemap_changefreq',
];
const FLAG_KEYS = ['robots_index', 'robots_follow', 'sitemap_include'];

const CHANGEFREQ = ['always', 'hourly', 'daily', 'weekly', 'monthly', 'yearly', 'never']
  .map((v) => ({ value: v, label: v[0].toUpperCase() + v.slice(1) }));
const TWITTER_CARDS = [
  { value: '', label: 'Automatic (large image when there is one)' },
  { value: 'summary_large_image', label: 'Large image' },
  { value: 'summary', label: 'Small square image' },
];

function formFrom(row, entry) {
  const f = {};
  for (const k of TEXT_KEYS) f[k] = row?.[k] ?? '';
  for (const k of FLAG_KEYS) f[k] = row ? Number(row[k]) !== 0 : true;
  f.sitemap_priority = row ? String(row.sitemap_priority ?? '') : String(entry?.priority ?? '0.5');
  if (!row) {
    f.sitemap_changefreq = entry?.changefreq || 'weekly';
    // A new row's twitter_card would be the column default; "automatic" is what an unconfigured page serves.
    f.twitter_card = '';
  }
  return f;
}

function jsonProblem(text) {
  if (!text || !text.trim()) return null;
  try {
    const v = JSON.parse(text);
    const arr = Array.isArray(v) ? v : [v];
    if (!arr.every((b) => b && typeof b === 'object' && !Array.isArray(b))) return 'Must be a JSON object, or an array of objects.';
    return null;
  } catch (e) {
    return `Not valid JSON — ${e.message}`;
  }
}

export default function SeoPageEditor() {
  const { pageKey } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [brand, setBrand] = useSeoBrand();
  const [form, setForm] = useState(null);
  const [errors, setErrors] = useState({});

  const { data, isLoading, isError, error } = useQuery({
    queryKey: qk.seo.page(brand, pageKey),
    queryFn: () => getSeoPage(brand, pageKey),
    enabled: Boolean(brand),
  });
  const loaded = data?.data;
  const entry = loaded?.entry;
  const fallbacks = loaded?.fallbacks || {};

  // Re-seed the form whenever the saved row (or the storefront) changes.
  useEffect(() => {
    if (loaded) setForm(formFrom(loaded.row, loaded.entry));
  }, [loaded]);

  const draft = useDebounced(form, 400);
  const previewBody = useMemo(
    () => (draft && brand ? { brand, pageKey, page: draft } : null),
    [draft, brand, pageKey],
  );
  const preview = useQuery({
    queryKey: qk.seo.preview(previewBody),
    queryFn: () => previewSeo(previewBody),
    enabled: Boolean(previewBody),
    placeholderData: keepPreviousData,
  });
  const resolved = preview.data?.data || loaded?.resolved || null;

  const invalidate = () => qc.invalidateQueries({ queryKey: qk.seo.root });

  const saveMut = useMutation({
    mutationFn: (body) => saveSeoPage(pageKey, body),
    onSuccess: () => { toast.ok('Saved', `${entry?.label || 'Page'} SEO updated.`); setErrors({}); invalidate(); },
    onError: (err) => { setErrors(errFields(err)); toast.err('Could not save', errMessage(err, 'Validation failed')); },
  });
  const resetMut = useMutation({
    mutationFn: () => resetSeoPage(brand, pageKey),
    onSuccess: () => { toast.ok('Reset', 'This page now uses the defaults.'); setErrors({}); invalidate(); },
    onError: (err) => toast.err('Could not reset', errMessage(err)),
  });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? (e.target.type === 'checkbox' ? e.target.checked : e.target.value) : e }));
  const sdProblem = form ? jsonProblem(form.structured_data) : null;

  function onSubmit(e) {
    e.preventDefault();
    if (sdProblem) { setErrors({ structured_data: sdProblem }); return; }
    saveMut.mutate({ brand, ...form });
  }

  async function onReset() {
    const ok = await confirm({
      title: 'Reset to defaults?',
      message: `Every field on "${entry?.label}" is cleared and the page falls back to the brand defaults.`,
      confirmLabel: 'Reset',
    });
    if (ok) resetMut.mutate();
  }

  const fb = (k) => <Fallback show={form && !String(form[k] || '').trim()} value={fallbacks[k]} />;

  return (
    <>
      <PageHead
        title={entry ? `Page SEO · ${entry.label}` : 'Page SEO'}
        subtitle={entry ? `SEO Management · ${entry.pattern}` : 'SEO Management'}
        actions={<button className="btn ghost" onClick={() => navigate(withBrand('/seo/pages', brand))}><Icon name="x" /> Back</button>}
      />
      <BrandBar brand={brand} onChange={setBrand} />
      {!brand ? <PickBrand /> : isError ? (
        <article className="panel glass"><div className="empty"><p className="field-error">{errMessage(error, 'Could not load this page.')}</p></div></article>
      ) : isLoading || !form ? (
        <article className="panel glass"><div className="empty">Loading…</div></article>
      ) : (
        <>
          <NoindexBanner reason={loaded.siteNoindexReason} brand={brand} />
          {entry.item ? (
            <div className="notice-banner info">
              This is the TEMPLATE every {entry.item === 'blog' ? 'blog post' : 'package'} falls back to. A {entry.item}
              {' '}with its own SEO fields (on its edit screen) overrides it; the preview uses a placeholder {entry.item} with none.
            </div>
          ) : null}
          {entry.optional ? (
            <div className="notice-banner info">
              This page exists only on some storefronts — include it in the sitemap only for the brand that actually has it.
            </div>
          ) : null}

          <div className="seo-split">
            <article className="panel glass">
              <form className="form-grid" onSubmit={onSubmit} style={{ gridTemplateColumns: 'repeat(2, minmax(0,1fr))' }}>
                <SectionTitle>Search</SectionTitle>
                <Field label="Title" full err={errors.title}>
                  <input value={form.title} onChange={set('title')} maxLength={255} placeholder={entry.label} />
                  <Counter value={resolved?.title || form.title} limit={TITLE_LIMIT} />
                  {fb('title')}
                  <div className="field-hint muted">The brand’s title template is applied to this once — the preview shows the result.</div>
                </Field>
                <Field label="Meta description" full err={errors.meta_description}>
                  <textarea rows={3} value={form.meta_description} onChange={set('meta_description')} maxLength={500} />
                  <Counter value={form.meta_description} limit={DESCRIPTION_LIMIT} />
                  {fb('meta_description')}
                </Field>
                <Field label="Keywords" err={errors.meta_keywords}>
                  <input value={form.meta_keywords} onChange={set('meta_keywords')} maxLength={500} placeholder="comma, separated" />
                  {fb('meta_keywords')}
                </Field>
                <Field label="H1 heading" err={errors.h1}>
                  <input value={form.h1} onChange={set('h1')} maxLength={255} />
                  {fb('h1')}
                </Field>
                <Field label="Canonical URL override" full err={errors.canonical_url}
                  hint="Leave blank unless this page's content lives at another URL. Must be absolute (https://…).">
                  <input value={form.canonical_url} onChange={set('canonical_url')} maxLength={500} placeholder="https://…" />
                  {fb('canonical_url')}
                </Field>

                <SectionTitle>Social</SectionTitle>
                <Field label="Social title (og:title)" err={errors.og_title}>
                  <input value={form.og_title} onChange={set('og_title')} maxLength={255} />
                  {fb('og_title')}
                </Field>
                <Field label="og:type" err={errors.og_type}>
                  <input value={form.og_type} onChange={set('og_type')} maxLength={30} placeholder="website" />
                  {fb('og_type')}
                </Field>
                <Field label="Social description (og:description)" full err={errors.og_description}>
                  <textarea rows={2} value={form.og_description} onChange={set('og_description')} maxLength={500} />
                  {fb('og_description')}
                </Field>
                <Field label="Social image (1200×630)" full err={errors.og_image_url}>
                  <ImageUpload value={form.og_image_url} onChange={set('og_image_url')} />
                  {fb('og_image_url')}
                </Field>
                <Field label="Twitter card" err={errors.twitter_card}>
                  <Select value={form.twitter_card} onChange={set('twitter_card')} options={TWITTER_CARDS} />
                </Field>
                <Field label="Twitter title" err={errors.twitter_title}>
                  <input value={form.twitter_title} onChange={set('twitter_title')} maxLength={255} />
                  {fb('twitter_title')}
                </Field>
                <Field label="Twitter description" full err={errors.twitter_description}>
                  <textarea rows={2} value={form.twitter_description} onChange={set('twitter_description')} maxLength={500} />
                  {fb('twitter_description')}
                </Field>
                <Field label="Twitter image" full err={errors.twitter_image_url}>
                  <ImageUpload value={form.twitter_image_url} onChange={set('twitter_image_url')} />
                  {fb('twitter_image_url')}
                </Field>

                <SectionTitle>Indexing</SectionTitle>
                <Field label="Search engines">
                  <label className="check">
                    <input type="checkbox" checked={form.robots_index} onChange={set('robots_index')} />
                    <span>Index this page</span>
                  </label>
                  <label className="check">
                    <input type="checkbox" checked={form.robots_follow} onChange={set('robots_follow')} />
                    <span>Follow its links</span>
                  </label>
                </Field>
                <Field label="Sitemap" err={errors.sitemap_include}>
                  <label className="check">
                    <input type="checkbox" checked={form.sitemap_include} onChange={set('sitemap_include')} />
                    <span>Include in sitemap.xml</span>
                  </label>
                </Field>
                <Field label="Sitemap priority (0.0 – 1.0)" err={errors.sitemap_priority}>
                  <input type="number" min={0} max={1} step={0.1} value={form.sitemap_priority} onChange={set('sitemap_priority')} />
                </Field>
                <Field label="Change frequency" err={errors.sitemap_changefreq}>
                  <Select value={form.sitemap_changefreq} onChange={set('sitemap_changefreq')} options={CHANGEFREQ} />
                </Field>

                <SectionTitle>Structured data</SectionTitle>
                <Field label="Extra JSON-LD" full err={errors.structured_data || sdProblem}
                  hint="Optional. A JSON object or an array of objects, added after the generated Organization / Breadcrumb blocks. Invalid JSON is ignored by the storefront.">
                  <textarea
                    className="seo-code"
                    rows={8}
                    value={form.structured_data}
                    onChange={set('structured_data')}
                    placeholder={'{\n  "@context": "https://schema.org",\n  "@type": "…"\n}'}
                    spellCheck={false}
                  />
                </Field>

                <div className="form-actions">
                  {loaded.row ? (
                    <button type="button" className="btn ghost" onClick={onReset} disabled={resetMut.isPending}>
                      {resetMut.isPending ? 'Resetting…' : 'Reset to defaults'}
                    </button>
                  ) : null}
                  <button className="btn primary" type="submit" disabled={saveMut.isPending}>
                    <Icon name="check" /> {saveMut.isPending ? 'Saving…' : 'Save'}
                  </button>
                </div>
              </form>
            </article>

            <aside className="seo-side">
              <article className="panel glass">
                <div className="seo-preview-label">Google result</div>
                <GooglePreview resolved={resolved} loading={preview.isFetching} />
                {resolved?.invalidJson?.length ? (
                  <div className="field-error" style={{ marginTop: 8 }}>
                    Ignored as invalid JSON: {resolved.invalidJson.join(', ')}
                  </div>
                ) : null}
              </article>
              <article className="panel glass">
                <div className="seo-preview-label">Social card</div>
                <SocialPreview resolved={resolved} />
                <div className="muted" style={{ marginTop: 8, fontSize: '.74rem' }}>
                  Brand defaults live in <Link to={withBrand('/seo/settings', brand)}>Global SEO</Link>.
                </div>
              </article>
            </aside>
          </div>
        </>
      )}
    </>
  );
}
